import React from 'react';
import {TestRunJson} from "../../api/json/TestRunJson"
import {Alert, Box, Divider, Paper, Stack, Tooltip, Typography} from "@mui/material"
import TemplateRenderer from "../templates/TemplateRenderer"
import TemplateRendererSection from "../templates/TemplateRendererSection"
import {DisplayFormat} from "../../api/enum/DisplayFormat"
import {useToasts} from "../../hooks/useToasts"
import {WarningAmberOutlined} from "@mui/icons-material"
import {TestRunRequests} from "../../api/requests/TestRunRequests"
import QualityCheckJson from "../../api/json/QualityCheckJson"
import {AnyTemplateJson} from "../../api/json/TemplateJson"
import ScoreInput from "./ScoreInput"
import TestRunActionMenu from "./TestRunActionMenu"
import SpaceBetween from "../common/SpaceBetween"
import {TimeUtil} from "../../library/TimeUtil"

interface TestRunCardProps {
    qualityCheckJson: QualityCheckJson
    template: AnyTemplateJson
    testRun: TestRunJson
    didUpdate: (testRun: TestRunJson) => void
    didDelete: (exampleId: number) => void
    limitMaxHeight?: boolean
}

const columnLabelSx = {textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600} as const

function TestRunCard({qualityCheckJson, template, testRun, didUpdate, didDelete, limitMaxHeight}: TestRunCardProps) {
    const maxHeightSx = limitMaxHeight ? {overflowY: "auto", maxHeight: "24rem"} : {}

    const toasts = useToasts()

    const updateScore = (testRun: TestRunJson, newScore: number | null) => {
        TestRunRequests.updateScore(qualityCheckJson.id, testRun.row.id, newScore ? (newScore - 1) * 25 : 0)
            .then(updated => {
                didUpdate(updated)
            })
            .catch(toasts.showError)
    }

    return (
        <Paper variant="outlined" sx={{p: 2}}>
            <SpaceBetween>
                <Typography variant="caption" color="text.secondary">{TimeUtil.ago(testRun.created)}</Typography>
                <TestRunActionMenu qualityCheckJson={qualityCheckJson}
                                   testRun={testRun}
                                   didUpdate={didUpdate}
                                   didDelete={didDelete}
                                   sx={{alignSelf: "flex-end"}}
                />
            </SpaceBetween>

            <Stack direction={{xs: "column", sm: "row"}}
                   spacing={2}
                   divider={<Divider orientation="vertical" flexItem/>}
                   sx={{mt: 0.5}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography variant="caption" color="text.secondary" sx={columnLabelSx}>Predicted</Typography>

                    {testRun.failed &&
                        <Alert severity="error" sx={{mt: 0.5}}>{testRun.failedReason}</Alert>
                    }

                    {testRun.output !== null &&
                        <Box sx={maxHeightSx}>
                            <TemplateRendererSection value={testRun.output}
                                                     display={DisplayFormat.PLAIN_TEXT}/>
                        </Box>
                    }

                    {testRun.output !== null &&
                        <Stack direction="row" spacing={2} alignItems="center" sx={{pt: 1}}>
                            <ScoreInput method={qualityCheckJson.scoringMethod}
                                        testRun={testRun}
                                        updateScore={updateScore}
                            />
                            {testRun.reason &&
                                <Typography variant="caption" color="text.secondary">{testRun.reason}</Typography>
                            }
                        </Stack>
                    }
                </Box>

                <Box sx={{flex: 1, minWidth: 0}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary" sx={columnLabelSx}>Expected</Typography>
                        {testRun.rowIssue &&
                            <Tooltip title="There may be an issue with this row.">
                                <WarningAmberOutlined color="warning" fontSize="small"/>
                            </Tooltip>
                        }
                    </Stack>
                    <Box sx={maxHeightSx}>
                        <TemplateRenderer dataSource={testRun.row.cells}
                                          templateParts={template}
                                          section="completion"/>
                    </Box>
                </Box>
            </Stack>

            <Divider sx={{my: 1.5}}/>

            <Typography variant="caption" color="text.secondary" sx={columnLabelSx}>Input</Typography>
            <TemplateRenderer dataSource={testRun.row.cells}
                              templateParts={template}
                              staticSystemPromptTreatment="hide"
                              section="prompt"
                              sx={{color: "text.secondary", ...maxHeightSx}}
            />
        </Paper>
    );
}

export default TestRunCard;
